// ─────────────────────────────────────────────────────────────────────────
// Motor de cálculo AQL — IRAM 15 (idéntica a ISO 2859-1 / MIL-STD-105E).
//
// ESTADO DE LAS TABLAS (léase antes de usar en producción/auditoría):
//   ✅ VERIFICADO — Tabla I (código de letra por tamaño de lote).
//   ✅ VERIFICADO — Tabla II-A, muestreo SIMPLE, inspección NORMAL, para
//      AQL de 0.065 a 6.5 (el rango que cubre prácticamente todo uso real
//      en planta: AQL típicos de gráfica/packaging son 1.5/2.5/4.0).
//   ⚠️ PARCIAL — Tabla III-A/B/C, muestreo DOBLE (Normal, Rigurosa y
//      Reducida). La cascada de valores Ac1/Re1→Ac2/Re2 y los tamaños de
//      muestra por letra SÍ se leyeron directo del PDF real (páginas
//      17-19, a 400dpi, mejor calidad de escaneo que II-B/II-C). Pero la
//      fórmula que dice en qué columna de AQL arranca el plan 1 para
//      cada letra (la diagonal letra↔AQL) NO quedó confirmada: dos casos
//      de verificación propios no cerraron entre sí bajo un
//      desplazamiento uniforme. No se carga ninguna tabla derivada de
//      esa fórmula hasta confirmarla. Ver TABLAS_IRAM15_III_DOBLE.md.
//   ❌ NO CARGADO — muestreo TIGHTENED (Tabla II-B) y REDUCED (Tabla
//      II-C) simples, y AQL fuera del rango 0.065–6.5. Se intentó
//      transcribir dos veces del mismo PDF (páginas 15-16): la primera
//      vez dio valores que resultaron parcialmente erróneos al re-leer
//      (letra B, AQL 2.5 salió distinto en cada lectura), y la segunda
//      re-lectura, más cuidadosa, siguió dando lecturas contradictorias
//      entre sí en varias celdas — el escaneo es denso ahí y las flechas
//      se prestan a error de conteo de fila incluso a alta resolución.
//      Antes de arriesgar un número mal leído en un dictamen real de
//      calidad, se decidió (con Adur) dejar esto afuera del código en
//      vez de cargar algo no confiable. Si hace falta más adelante: una
//      foto/escaneo más nítido de esas dos páginas, o releer con más
//      tiempo, resolvería esto — no es un límite permanente.
//   ⛔ FUERA DE ALCANCE (decisión explícita) — Tabla IV, muestreo
//      MÚLTIPLE. No se usa en la práctica de control de calidad de la
//      pyme; Simple + Doble cubren el uso real.
//
// Mientras tanto: la calculadora funciona completa para Simple/Normal
// (el caso de uso de todos los días — AQL 0.065 a 6.5). Doble, Tightened
// y Reduced todavía no calculan nada real (planDoble() y planSimple con
// severidad ≠ normal devuelven disponible:false con el motivo). No se
// inventa ningún número acá.
// ─────────────────────────────────────────────────────────────────────────

// ── Tabla I — Código de letra según tamaño de lote y nivel de inspección ──
// Niveles especiales S-1 a S-4 (para lotes chicos / inspección destructiva
// o muy costosa) y niveles generales I (menos estricto), II (el que se usa
// por defecto en la enorme mayoría de los casos) y III (más estricto).
const TABLA_I = [
  { hasta: 8, "S-1": "A", "S-2": "A", "S-3": "A", "S-4": "A", I: "A", II: "A", III: "B" },
  { hasta: 15, "S-1": "A", "S-2": "A", "S-3": "A", "S-4": "A", I: "A", II: "B", III: "C" },
  { hasta: 25, "S-1": "A", "S-2": "A", "S-3": "B", "S-4": "B", I: "B", II: "C", III: "D" },
  { hasta: 50, "S-1": "A", "S-2": "B", "S-3": "B", "S-4": "C", I: "C", II: "D", III: "E" },
  { hasta: 90, "S-1": "B", "S-2": "B", "S-3": "C", "S-4": "C", I: "C", II: "E", III: "F" },
  { hasta: 150, "S-1": "B", "S-2": "B", "S-3": "C", "S-4": "D", I: "D", II: "F", III: "G" },
  { hasta: 280, "S-1": "B", "S-2": "C", "S-3": "D", "S-4": "E", I: "E", II: "G", III: "H" },
  { hasta: 500, "S-1": "B", "S-2": "C", "S-3": "D", "S-4": "E", I: "F", II: "H", III: "J" },
  { hasta: 1200, "S-1": "C", "S-2": "C", "S-3": "E", "S-4": "F", I: "G", II: "J", III: "K" },
  { hasta: 3200, "S-1": "C", "S-2": "D", "S-3": "E", "S-4": "G", I: "H", II: "K", III: "L" },
  { hasta: 10000, "S-1": "C", "S-2": "D", "S-3": "F", "S-4": "G", I: "J", II: "L", III: "M" },
  { hasta: 35000, "S-1": "C", "S-2": "D", "S-3": "F", "S-4": "H", I: "K", II: "M", III: "N" },
  { hasta: 150000, "S-1": "D", "S-2": "E", "S-3": "G", "S-4": "J", I: "L", II: "N", III: "P" },
  { hasta: 500000, "S-1": "D", "S-2": "E", "S-3": "G", "S-4": "J", I: "M", II: "P", III: "Q" },
  { hasta: Infinity, "S-1": "D", "S-2": "E", "S-3": "H", "S-4": "K", I: "N", II: "Q", III: "R" },
];

export function codigoLetra(tamanoLote, nivel = "II") {
  const fila = TABLA_I.find(f => tamanoLote <= f.hasta);
  return fila ? fila[nivel] : null;
}

// ── Tabla II-A — Muestreo SIMPLE, inspección NORMAL (✅ verificada) ──
// Tamaño de muestra por letra + Ac/Re para AQL 0.065 a 6.5.
// "↓" = no hay plan definido en este rango para esa letra con ese AQL —
// en la norma real corresponde usar el plan de la letra siguiente
// (no está resuelto automáticamente todavía, ver limitaciones abajo).
const TABLA_II_A_NORMAL_SIMPLE = {
  A: { n: 2, planes: { 2.5: [0, 1], 4.0: [0, 1], 6.5: [1, 2] } },
  B: { n: 3, planes: { 1.5: [0, 1], 2.5: [0, 1], 4.0: [0, 1], 6.5: [1, 2] } },
  C: { n: 5, planes: { 1.0: [0, 1], 1.5: [0, 1], 2.5: [0, 1], 4.0: [1, 2], 6.5: [2, 3] } },
  D: { n: 8, planes: { 0.65: [0, 1], 1.0: [0, 1], 1.5: [0, 1], 2.5: [1, 2], 4.0: [2, 3], 6.5: [3, 4] } },
  E: { n: 13, planes: { 0.40: [0, 1], 0.65: [0, 1], 1.0: [0, 1], 1.5: [1, 2], 2.5: [2, 3], 4.0: [3, 4], 6.5: [5, 6] } },
  F: { n: 20, planes: { 0.25: [0, 1], 0.40: [0, 1], 0.65: [0, 1], 1.0: [1, 2], 1.5: [2, 3], 2.5: [3, 4], 4.0: [5, 6], 6.5: [7, 8] } },
  G: { n: 32, planes: { 0.15: [0, 1], 0.25: [0, 1], 0.40: [0, 1], 0.65: [1, 2], 1.0: [2, 3], 1.5: [3, 4], 2.5: [5, 6], 4.0: [7, 8], 6.5: [10, 11] } },
  H: { n: 50, planes: { 0.10: [0, 1], 0.15: [0, 1], 0.25: [0, 1], 0.40: [1, 2], 0.65: [2, 3], 1.0: [3, 4], 1.5: [5, 6], 2.5: [7, 8], 4.0: [10, 11], 6.5: [14, 15] } },
  J: { n: 80, planes: { 0.065: [0, 1], 0.10: [0, 1], 0.15: [0, 1], 0.25: [1, 2], 0.40: [2, 3], 0.65: [3, 4], 1.0: [5, 6], 1.5: [7, 8], 2.5: [10, 11], 4.0: [14, 15], 6.5: [21, 22] } },
  K: { n: 125, planes: { 0.065: [0, 1], 0.10: [0, 1], 0.15: [1, 2], 0.25: [2, 3], 0.40: [3, 4], 0.65: [5, 6], 1.0: [7, 8], 1.5: [10, 11], 2.5: [14, 15], 4.0: [21, 22] } },
  L: { n: 200, planes: { 0.065: [0, 1], 0.10: [1, 2], 0.15: [2, 3], 0.25: [3, 4], 0.40: [5, 6], 0.65: [7, 8], 1.0: [10, 11], 1.5: [14, 15], 2.5: [21, 22] } },
  M: { n: 315, planes: { 0.065: [1, 2], 0.10: [2, 3], 0.15: [3, 4], 0.25: [5, 6], 0.40: [7, 8], 0.65: [10, 11], 1.0: [14, 15], 1.5: [21, 22] } },
  N: { n: 500, planes: { 0.065: [2, 3], 0.10: [3, 4], 0.15: [5, 6], 0.25: [7, 8], 0.40: [10, 11], 0.65: [14, 15], 1.0: [21, 22] } },
  P: { n: 800, planes: { 0.065: [3, 4], 0.10: [5, 6], 0.15: [7, 8], 0.25: [10, 11], 0.40: [14, 15], 0.65: [21, 22] } },
  Q: { n: 1250, planes: { 0.065: [5, 6], 0.10: [7, 8], 0.15: [10, 11], 0.25: [14, 15], 0.40: [21, 22] } },
  R: { n: 2000, planes: { 0.065: [7, 8], 0.10: [10, 11], 0.15: [14, 15], 0.25: [21, 22] } },
};

// AQLs habituales en gráfica/packaging — los que sí están cargados.
export const AQL_DISPONIBLES = [0.065, 0.10, 0.15, 0.25, 0.40, 0.65, 1.0, 1.5, 2.5, 4.0, 6.5];

// ⚠️ Placeholders — ver cabecera (Tightened/Reduced simple no cargados todavía).
export const TABLA_II_B_TIGHTENED_SIMPLE = null; // TODO: reintentar transcripción con fuente más nítida
export const TABLA_II_C_REDUCED_SIMPLE = null;   // TODO: ídem

// ⚠️ Tabla III (Doble) — la cascada Ac1/Re1→Ac2/Re2 y los tamaños de
// muestra por letra SÍ están confirmados contra el PDF real (ver
// TABLAS_IRAM15_III_DOBLE.md). Lo que NO quedó resuelto de forma
// confiable es la fórmula exacta que dice en qué columna de AQL arranca
// el plan 1 para cada letra (la diagonal): dos casos de verificación que
// yo misma escribí en ese documento (letra G a AQL 6,5, y letra K a AQL
// 1,5) no son consistentes entre sí bajo un desplazamiento uniforme de
// 1 letra por paso de AQL. Antes de que esto calcule un n/Ac/Re real
// para un dictamen de lote, hay que confirmar la alineación letra↔AQL
// releyendo la tabla una vez más con calma (o pidiéndole a Adur que
// chequee 2-3 celdas puntuales contra el PDF impreso). Mientras tanto
// planDoble() sigue sin datos — no se carga una fórmula sin confirmar.
export const TABLA_III_DOBLE = null; // TODO: cargar una vez resuelta la alineación letra↔AQL

// Devuelve { n, Ac, Re, disponible } para muestreo simple, severidad normal.
// Si la combinación letra+AQL no está en la tabla ("↓" en la norma real),
// devuelve disponible:false — en la norma corresponde subir a la letra
// siguiente con muestra mayor; no resuelto automático todavía (ver limitaciones).
export function planSimpleNormal(letra, aql) {
  const fila = TABLA_II_A_NORMAL_SIMPLE[letra];
  if (!fila) return { disponible: false, motivo: `Letra ${letra} no cargada` };
  const plan = fila.planes[aql];
  if (!plan) return { disponible: false, motivo: `AQL ${aql} no definido para letra ${letra} en esta tabla (rango verificado: 0.065–6.5). Puede requerir subir de letra o cargar el rango extendido.`, n: fila.n };
  return { disponible: true, n: fila.n, Ac: plan[0], Re: plan[1] };
}

export function planSimple(letra, aql, severidad = "normal") {
  if (severidad === "normal") return planSimpleNormal(letra, aql);
  return { disponible: false, motivo: `Tabla de severidad "${severidad}" todavía no cargada — pendiente de verificar contra la norma original (ver cabecera de aqlIRAM15.js).` };
}

export function planDoble() {
  return { disponible: false, motivo: "Muestreo doble todavía no cargado — pendiente de verificar contra la norma original (ver cabecera de aqlIRAM15.js)." };
}

// ── Reglas de cambio de severidad (normal ↔ tightened ↔ reduced) ──
// Estas SÍ son reglas textuales de la cláusula 8 de la norma (no una tabla
// numérica de miles de celdas), reproducidas tal como las publica el
// estándar:
//   normal → tightened: 2 de los últimos 5 lotes consecutivos rechazados
//     en inspección normal.
//   tightened → normal: 5 lotes consecutivos aceptados en inspección
//     tightened.
//   tightened → detener inspección: 5 lotes consecutivos en tightened y
//     todavía no se pudo volver a normal (la norma indica suspender la
//     entrega hasta que se corrija el proceso).
//   normal → reduced: los últimos 10 lotes consecutivos aceptados en
//     normal, la producción es estable, y el organismo/autoridad de
//     calidad lo autoriza explícitamente.
//   reduced → normal: un lote rechazado, o producción irregular/atrasada,
//     bajo inspección reduced.
export function evaluarCambioSeveridad(historialLotes, severidadActual) {
  const ultimos5 = historialLotes.slice(-5);
  const ultimos10 = historialLotes.slice(-10);
  const rechazosUlt5 = ultimos5.filter(l => l.resultado === "rechazado").length;

  if (severidadActual === "normal" && rechazosUlt5 >= 2 && ultimos5.length === 5) {
    return { cambiar: true, a: "tightened", motivo: `${rechazosUlt5} de los últimos 5 lotes rechazados en inspección normal — corresponde pasar a tightened (rigurosa).` };
  }
  if (severidadActual === "tightened") {
    const ultimos5T = historialLotes.filter(l => l.severidad === "tightened").slice(-5);
    if (ultimos5T.length === 5 && ultimos5T.every(l => l.resultado === "aceptado")) {
      return { cambiar: true, a: "normal", motivo: "5 lotes consecutivos aceptados en tightened — puede volver a normal." };
    }
  }
  if (severidadActual === "normal" && ultimos10.length === 10 && ultimos10.every(l => l.resultado === "aceptado")) {
    return { cambiar: true, a: "reduced", motivo: "10 lotes consecutivos aceptados en normal — es ELEGIBLE para pasar a reduced (todavía requiere autorización explícita de Calidad, no es automático)." };
  }
  if (severidadActual === "reduced" && historialLotes[historialLotes.length - 1]?.resultado === "rechazado") {
    return { cambiar: true, a: "normal", motivo: "Lote rechazado bajo inspección reduced — vuelve a normal." };
  }
  return { cambiar: false };
}
